// Mercado Libre desde el Chat — ADR-2026-09-28-mercadolibre-en-el-chat.
//
// El vendedor le habla al agente y opera su Mercado Libre: ver lo que tiene publicado, traerlo a
// Atlas, subir fotos generadas, cambiar precio o descripción y ver la calidad. Todo lo hace
// atlas-backend con el token de la conexión; acá no hay red ni React: lo de afuera se inyecta,
// así cada regla se prueba sola (mismo molde que `tiendanubeEnElChat.ts`, sin compartir código:
// son integraciones separadas).
//
// Viajan por `herramientasExtra`, SÓLO si la marca tiene Mercado Libre conectado (y la persona es
// dev tester mientras se prueba). Las que escriben en Mercado Libre usan la confirmación
// verificable con claves de acción propias: la primera llamada avisa y no toca nada; sólo actúa
// si la persona contestó que sí DESPUÉS del aviso.
import type { HerramientaChat } from './tipos';
import { anotarAviso, consumirAviso, puedeActuar } from './confirmacionVerificable';
import type { BloqueContenido } from '../pages/v2/lotes/verProductos';
import type { EstadoDeMercadoLibre } from '../lib/mercadoLibreConexionLogica';
import type { ListadoDeMl, PublicacionDeMl } from '../lib/mercadoLibreCatalogoLogica';
import type { PublicacionParaSubir, ResultadoDeSubida } from '../lib/mercadoLibreFotosLogica';
import type { CalidadDeMl, ResultadoCampo } from '../lib/mercadoLibreDatosLogica';

export const NOMBRE_CONECTAR_MERCADOLIBRE = 'conectar_mercadolibre';
export const NOMBRE_VER_PUBLICACIONES_ML = 'ver_publicaciones_de_mercadolibre';
export const NOMBRE_TRAER_DE_ML = 'traer_de_mercadolibre';
export const NOMBRE_SUBIR_FOTOS_A_ML = 'subir_fotos_a_mercadolibre';
export const NOMBRE_CAMBIAR_DATOS_EN_ML = 'cambiar_precio_o_descripcion_en_mercadolibre';
export const NOMBRE_VER_CALIDAD_ML = 'ver_calidad_en_mercadolibre';

/** Ruta de la tarjeta de conectar: abre el modal solo (deep-link de Mi marca). */
export const RUTA_CONECTAR_MERCADOLIBRE = '/mi-marca?conectar=mercadolibre';

const ID_DE_PUBLICACION = { type: 'string', description: 'El id de la publicación en Mercado Libre (empieza con MLA).' };

export const HERRAMIENTA_CONECTAR_MERCADOLIBRE: HerramientaChat = {
  name: NOMBRE_CONECTAR_MERCADOLIBRE,
  description:
    'Muestra una tarjeta con el botón para conectar (o reconectar) su cuenta de Mercado Libre desde Mi marca. NO conecta nada: el toque lo da el usuario, y tiene que ser el DUEÑO de la cuenta de Mercado Libre (un colaborador no puede). Usala cuando pida algo de su Mercado Libre y no esté conectado, o cuando haya que reconectar. No cuesta créditos.',
  input_schema: { type: 'object', properties: {} },
};

export const HERRAMIENTA_VER_PUBLICACIONES_ML: HerramientaChat = {
  name: NOMBRE_VER_PUBLICACIONES_ML,
  description:
    'Lista las publicaciones de su Mercado Libre: id, título, precio, colores y si ya están en Atlas. Sólo lee, no cambia nada. Usala para saber qué tiene publicado o para encontrar el id de una publicación antes de subirle fotos o cambiarle el precio. No cuesta créditos.',
  input_schema: {
    type: 'object',
    properties: {
      estado: { type: 'string', enum: ['active', 'paused'], description: 'active (por defecto) o paused.' },
      buscar: { type: 'string', description: 'Palabras del título, para filtrar.' },
    },
  },
};

export const HERRAMIENTA_TRAER_DE_ML: HerramientaChat = {
  name: NOMBRE_TRAER_DE_ML,
  description:
    'Trae a Atlas publicaciones de su Mercado Libre como productos (una variante por color, con sus fotos), para generar con ellas. Escribe en Atlas, no en Mercado Libre. NUNCA trae en la primera llamada: sin `publicaciones` te muestro las que no están en Atlas, con foto; con `publicaciones` y sin confirmar te devuelvo el aviso para mostrárselo. Recién si en su PRÓXIMO mensaje dice que sí, me volvés a llamar con las mismas publicaciones y `confirmado` en true. No cuesta créditos.',
  input_schema: {
    type: 'object',
    properties: {
      publicaciones: { type: 'array', items: { type: 'string' }, description: 'Los ids (MLA…) a traer.' },
      confirmado: { type: 'boolean', description: 'true sólo si el usuario dijo que sí DESPUÉS de ver el aviso.' },
    },
  },
};

export const HERRAMIENTA_SUBIR_FOTOS_A_ML: HerramientaChat = {
  name: NOMBRE_SUBIR_FOTOS_A_ML,
  description:
    'Sube fotos que ya generaste a una publicación de su Mercado Libre. Por defecto como secundarias: se AGREGAN al final y no se borra ninguna. `imagenes` son los image_id de las fotos (los mismos que usás como referencia con source "generada"). Si la publicación tiene colores, hace falta `variante` (te la doy si falta). Con `portada` en true, Mercado Libre la revisa antes y si no pasa va como secundaria. La primera llamada avisa y no sube nada; si en su PRÓXIMO mensaje dice que sí, me volvés a llamar igual con `confirmado` en true. No cuesta créditos.',
  input_schema: {
    type: 'object',
    properties: {
      publicacion: ID_DE_PUBLICACION,
      imagenes: { type: 'array', items: { type: 'string' }, description: 'Los image_id de las fotos (hasta 10).' },
      variante: { type: 'string', description: 'El id de la variante (color) de la publicación, si tiene.' },
      portada: { type: 'boolean', description: 'true para que la primera foto sea la portada.' },
      confirmado: { type: 'boolean', description: 'true sólo si el usuario dijo que sí DESPUÉS de ver el aviso.' },
    },
    required: ['publicacion', 'imagenes'],
  },
};

export const HERRAMIENTA_CAMBIAR_DATOS_EN_ML: HerramientaChat = {
  name: NOMBRE_CAMBIAR_DATOS_EN_ML,
  description:
    'Cambia el precio y/o la descripción de una publicación de su Mercado Libre. Se ve apenas termina y desde Atlas no se deshace. La descripción va en texto plano (sin emojis ni formato: la limpio yo). La primera llamada avisa y no cambia nada; si en su PRÓXIMO mensaje dice que sí, me volvés a llamar igual con `confirmado` en true. Si Mercado Libre avisa algo más (precio muy distinto, una promoción que se cae), te lo digo y hace falta otro sí. No cuesta créditos.',
  input_schema: {
    type: 'object',
    properties: {
      publicacion: ID_DE_PUBLICACION,
      precio: { type: 'number', description: 'Precio nuevo en pesos, sin puntos ni signo.' },
      descripcion: { type: 'string', description: 'Descripción nueva completa (reemplaza la actual).' },
      confirmado: { type: 'boolean', description: 'true sólo si el usuario dijo que sí DESPUÉS de ver el aviso.' },
    },
    required: ['publicacion'],
  },
};

export const HERRAMIENTA_VER_CALIDAD_ML: HerramientaChat = {
  name: NOMBRE_VER_CALIDAD_ML,
  description:
    'Muestra el puntaje de calidad que Mercado Libre le pone a una publicación, qué le falta y las visitas de los últimos 30 días. Sólo lee. Si dice que faltan fotos, es el momento de ofrecer generarlas. No cuesta créditos.',
  input_schema: { type: 'object', properties: { publicacion: ID_DE_PUBLICACION }, required: ['publicacion'] },
};

/** Las que viajan cuando la marca TIENE Mercado Libre conectado. */
export const HERRAMIENTAS_DE_MERCADOLIBRE: HerramientaChat[] = [
  HERRAMIENTA_VER_PUBLICACIONES_ML,
  HERRAMIENTA_TRAER_DE_ML,
  HERRAMIENTA_SUBIR_FOTOS_A_ML,
  HERRAMIENTA_CAMBIAR_DATOS_EN_ML,
  HERRAMIENTA_VER_CALIDAD_ML,
];

// ════════════════════════════════════════════════════════════════════════════
// Bloque del prompt
// ════════════════════════════════════════════════════════════════════════════

const TITULO = '══════════ MERCADO LIBRE ══════════';

/** Qué se le dice al modelo en cada turno. null = nada (sin conexión, o no se pudo averiguar). */
export function bloqueDeMercadoLibre(estado: EstadoDeMercadoLibre | null, seDesconecto = false): string | null {
  if (!estado || estado.error || !estado.disponible) return null;
  const cuenta = estado.nickname ? `la cuenta ${estado.nickname}` : 'su cuenta';
  if (estado.estado === 'reconectar') {
    return `${TITULO}\nLa marca tenía conectada ${cuenta}, pero Mercado Libre pidió volver a conectarla. Si pide algo de Mercado Libre, mostrale la tarjeta con \`${NOMBRE_CONECTAR_MERCADOLIBRE}\`.`;
  }
  if (!estado.conectada) {
    if (!seDesconecto) return null;
    return `${TITULO}\nLa marca YA NO tiene Mercado Libre conectado: se desconectó durante esta charla. No digas que sigue conectado. Si quiere volver a usarlo, mostrale la tarjeta con \`${NOMBRE_CONECTAR_MERCADOLIBRE}\`.`;
  }
  return `${TITULO}\nLa marca tiene conectada ${cuenta} de Mercado Libre. Podés ver sus publicaciones, traerlas a Atlas, subirles fotos, cambiar precio o descripción y ver su calidad (\`${NOMBRE_VER_PUBLICACIONES_ML}\` y las demás). Antes de usarlas cargá la skill \`mercadolibre\`. Publicar un lanzamiento en Mercado Libre es otra cosa: skill \`lanzamientos\`. Nada de Mercado Libre cuesta créditos. No lo ofrezcas en cada respuesta: sólo cuando venga al caso.`;
}

// ════════════════════════════════════════════════════════════════════════════
// Resolvers
// ════════════════════════════════════════════════════════════════════════════

export interface DepsMercadoLibre {
  listar: (estado: 'active' | 'paused', agrupar: boolean) => Promise<ListadoDeMl>;
  importar: (elegidas: Array<{ item_ids: string[] }>) => Promise<string>;
  describir: (itemId: string) => Promise<PublicacionParaSubir>;
  subir: (p: {
    itemId: string;
    fotos: Array<{ imagenId: string; posicion: 'portada' | 'secundaria' }>;
    variationId: string | null;
  }) => Promise<string | null>;
  esperarSubida: (executionId: string) => Promise<ResultadoDeSubida | null>;
  cambiar: (p: { itemId: string; precio?: number | null; descripcion?: string | null; confirmado: boolean }) => Promise<string>;
  esperarCambio: (executionId: string) => Promise<{ precio?: ResultadoCampo; descripcion?: ResultadoCampo } | null>;
  calidad: (itemId: string) => Promise<CalidadDeMl>;
  visitas: (itemId: string) => Promise<number>;
}

export interface CtxConfirmacion {
  turno: number | null;
  ultimoMensaje?: string;
}

type Resultado = { __texto: string } | { __bloques: BloqueContenido[] };
const texto = (t: string): Resultado => ({ __texto: t });

const RE_ITEM = /^MLA\d{5,15}$/;

/** Lo que se le dice al modelo cuando falla la llamada al backend. */
export function textoDeError(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;
  if (code === 'reconectar' || code === 'sin_conexion') {
    return `Mercado Libre no está conectado o pidió reconectar. Decíselo y mostrale la tarjeta con \`${NOMBRE_CONECTAR_MERCADOLIBRE}\`.`;
  }
  if (code === 'no_disponible') return 'Mercado Libre no está disponible desde Atlas en este momento. Decíselo así.';
  if (code === 'publicacion_bloqueada' || code === 'publicacion_no_encontrada') {
    return `${(err as Error).message} Decíselo así.`;
  }
  return 'No se pudo consultar Mercado Libre en este momento. Decíselo así, sin inventar una causa, y ofrecé reintentar más tarde. No repitas la herramienta ahora.';
}

function linea(p: PublicacionDeMl): string {
  const precio = typeof p.precio === 'number' ? ` · $ ${p.precio.toLocaleString('es-AR')}` : '';
  const colores = p.colores.length ? ` · colores: ${p.colores.join(', ')}` : '';
  const extra = [p.producto_id ? 'ya está en Atlas' : null, p.es_catalogo ? 'de catálogo (no acepta fotos propias)' : null]
    .filter(Boolean)
    .join(', ');
  return `- ${p.item_ids[0]} — ${p.titulo}${precio}${colores}${extra ? ` (${extra})` : ''}`;
}

function filtrar(lista: PublicacionDeMl[], buscar: string): PublicacionDeMl[] {
  const palabras = buscar
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/\s+/)
    .filter(Boolean);
  if (palabras.length === 0) return lista;
  return lista.filter((p) => {
    const t = p.titulo.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    return palabras.every((w) => t.includes(w));
  });
}

const MAX_LINEAS = 40;

export async function resolverVerPublicaciones(input: unknown, deps: DepsMercadoLibre): Promise<Resultado> {
  const i = (input ?? {}) as { estado?: unknown; buscar?: unknown };
  const estado = i.estado === 'paused' ? 'paused' : 'active';
  let listado: ListadoDeMl;
  try {
    listado = await deps.listar(estado, false);
  } catch (err) {
    return texto(textoDeError(err));
  }
  const lista = filtrar(listado.publicaciones, typeof i.buscar === 'string' ? i.buscar : '');
  if (lista.length === 0) {
    return texto(estado === 'active' ? 'No encontré publicaciones activas con eso.' : 'No encontré publicaciones pausadas con eso.');
  }
  const cortada = lista.length > MAX_LINEAS ? `\n(y ${lista.length - MAX_LINEAS} más: pedile que diga cuál busca)` : '';
  const truncado = listado.truncado ? `\nOjo: tiene ${listado.total} publicaciones y leí las ${listado.publicaciones.length} más recientes.` : '';
  return texto(
    `Publicaciones ${estado === 'active' ? 'activas' : 'pausadas'} de su Mercado Libre (id — título):\n${lista
      .slice(0, MAX_LINEAS)
      .map(linea)
      .join('\n')}${cortada}${truncado}\nNo le muestres los ids salvo que los pida: son para vos.`,
  );
}

const MAX_A_TRAER = 30;

export async function resolverTraerDeMl(input: unknown, deps: DepsMercadoLibre, ctx: CtxConfirmacion): Promise<Resultado> {
  const i = (input ?? {}) as { publicaciones?: unknown; confirmado?: unknown };
  const pedidos = Array.isArray(i.publicaciones)
    ? [...new Set(i.publicaciones.filter((x): x is string => typeof x === 'string' && RE_ITEM.test(x.trim())).map((x) => x.trim()))]
    : [];

  let listado: ListadoDeMl;
  try {
    listado = await deps.listar('active', true);
  } catch (err) {
    return texto(textoDeError(err));
  }

  // Sin pedido: se muestran las que no están en Atlas, con foto, para que elija.
  if (pedidos.length === 0) {
    const faltan = listado.publicaciones.filter((p) => !p.producto_id).slice(0, 12);
    if (faltan.length === 0) return texto('Todas sus publicaciones activas ya están en Atlas. Decíselo.');
    return {
      __bloques: [
        {
          type: 'text',
          text: `Estas publicaciones de Mercado Libre todavía no están en Atlas (id — título). Mostráselas y preguntá cuáles quiere traer; puede que alguna ya la tenga cargada con otro nombre, y eso sólo lo ve él mirando la foto:\n${faltan.map(linea).join('\n')}`,
        },
        ...faltan.filter((p) => p.miniatura).map((p) => ({ type: 'image' as const, source: { type: 'url' as const, url: p.miniatura! } })),
      ],
    };
  }

  // Cada id pedido se completa con su familia (en el modelo nuevo una familia es un producto).
  const elegidas: PublicacionDeMl[] = [];
  const noEncontradas: string[] = [];
  for (const id of pedidos) {
    const p = listado.publicaciones.find((x) => x.item_ids.includes(id));
    if (!p) noEncontradas.push(id);
    else if (!elegidas.includes(p)) elegidas.push(p);
  }
  const yaEstan = elegidas.filter((p) => p.producto_id);
  const aTraer = elegidas.filter((p) => !p.producto_id).slice(0, MAX_A_TRAER);
  const notas = [
    noEncontradas.length ? `No encontré entre las activas: ${noEncontradas.join(', ')}.` : '',
    yaEstan.length ? `Ya están en Atlas (no las traigo de nuevo): ${yaEstan.map((p) => p.titulo).join(', ')}.` : '',
  ]
    .filter(Boolean)
    .join(' ');
  if (aTraer.length === 0) return texto(`No hay nada para traer. ${notas}`.trim());

  const clave = aTraer
    .map((p) => p.item_ids.join('+'))
    .sort()
    .join(',');
  if (!puedeActuar({ accion: NOMBRE_TRAER_DE_ML, clave, confirmado: i.confirmado === true, turno: ctx.turno, ultimoMensaje: ctx.ultimoMensaje })) {
    anotarAviso(NOMBRE_TRAER_DE_ML, clave, ctx.turno);
    return {
      __bloques: [
        {
          type: 'text',
          text: `NO TRAJE NADA TODAVÍA. Voy a traer a Atlas ${aTraer.length === 1 ? 'esta publicación' : `estas ${aTraer.length} publicaciones`}: ${aTraer
            .map((p) => p.titulo)
            .join(', ')}. Mostráselas (fotos abajo) y preguntale si las traigo; si alguna ya la tiene cargada en Atlas con otro nombre, conviene vincularla desde Agregar varios en vez de traerla. Si en su próximo mensaje dice que sí, volvé a llamarme con las mismas publicaciones y confirmado en true. ${notas}`.trim(),
        },
        ...aTraer.filter((p) => p.miniatura).map((p) => ({ type: 'image' as const, source: { type: 'url' as const, url: p.miniatura! } })),
      ],
    };
  }
  consumirAviso(NOMBRE_TRAER_DE_ML);
  try {
    await deps.importar(aTraer.map((p) => ({ item_ids: p.item_ids })));
  } catch (err) {
    return texto(textoDeError(err));
  }
  return texto(
    `Mandé a traer ${aTraer.length === 1 ? '1 producto' : `${aTraer.length} productos`} de Mercado Libre: ${aTraer
      .map((p) => p.titulo)
      .join(', ')}. Tardan un rato en aparecer en Atlas: decí que los estás trayendo, NUNCA que ya están. ${notas}`.trim(),
  );
}

export async function resolverSubirFotosAMl(input: unknown, deps: DepsMercadoLibre, ctx: CtxConfirmacion): Promise<Resultado> {
  const i = (input ?? {}) as { publicacion?: unknown; imagenes?: unknown; variante?: unknown; portada?: unknown; confirmado?: unknown };
  const itemId = typeof i.publicacion === 'string' ? i.publicacion.trim() : '';
  if (!RE_ITEM.test(itemId)) {
    return texto(`Falta la publicación. Buscala con \`${NOMBRE_VER_PUBLICACIONES_ML}\` o preguntale cuál.`);
  }
  const imagenes = Array.isArray(i.imagenes)
    ? [...new Set(i.imagenes.filter((x): x is string => typeof x === 'string' && x.trim().length > 0).map((x) => x.trim()))]
    : [];
  if (imagenes.length === 0) return texto('Falta decir qué fotos subir. Preguntale cuáles, o mostrale las últimas.');
  if (imagenes.length > 10) return texto('Puedo subir hasta 10 fotos por vez. Preguntale cuáles van primero.');

  let pub: PublicacionParaSubir;
  try {
    pub = await deps.describir(itemId);
  } catch (err) {
    return texto(textoDeError(err));
  }
  if (pub.bloqueo) return texto(`${pub.bloqueo} Decíselo así; no hay forma de subirle fotos desde Atlas.`);
  if (pub.fotos + imagenes.length > pub.max_fotos) {
    return texto(
      `"${pub.titulo}" ya tiene ${pub.fotos} fotos y Mercado Libre acepta hasta ${pub.max_fotos}. Entran ${Math.max(pub.max_fotos - pub.fotos, 0)} más: preguntale cuáles, o que saque alguna allá primero.`,
    );
  }
  const variante = typeof i.variante === 'string' ? i.variante.trim() : '';
  let variationId: string | null = null;
  if (pub.colores.length > 1) {
    const color = pub.colores.find((c) => c.variation_id === variante);
    if (!color) {
      return texto(
        `"${pub.titulo}" tiene variantes y la foto va a una sola (la de un color no puede ir a otro). Preguntale a cuál; después me volvés a llamar con \`variante\`:\n${pub.colores
          .map((c) => `- ${c.variation_id} — ${c.etiqueta}`)
          .join('\n')}`,
      );
    }
    variationId = color.variation_id;
  } else if (pub.colores.length === 1) {
    variationId = pub.colores[0]!.variation_id;
  }
  const portada = i.portada === true;
  const etiquetaColor = variationId ? pub.colores.find((c) => c.variation_id === variationId)?.etiqueta : null;
  const clave = `${itemId}:${variationId ?? ''}:${portada ? 'p' : 's'}:${[...imagenes].sort().join('|')}`;
  if (!puedeActuar({ accion: NOMBRE_SUBIR_FOTOS_A_ML, clave, confirmado: i.confirmado === true, turno: ctx.turno, ultimoMensaje: ctx.ultimoMensaje })) {
    anotarAviso(NOMBRE_SUBIR_FOTOS_A_ML, clave, ctx.turno);
    const lugar = portada
      ? 'la primera como PORTADA (Mercado Libre la revisa antes: si no pasa, va como secundaria) y el resto al final'
      : 'al final, como fotos secundarias: no se borra ni se mueve ninguna de las que ya tiene';
    return texto(
      `NO SUBÍ NADA TODAVÍA. Voy a subir ${imagenes.length === 1 ? '1 foto' : `${imagenes.length} fotos`} a "${pub.titulo}"${etiquetaColor ? ` (${etiquetaColor})` : ''}, ${lugar}. Se ve en su publicación apenas termina y desde Atlas no se deshace. Contáselo y preguntale si la subo. Si en su próximo mensaje dice que sí, volvé a llamarme igual con confirmado en true.`,
    );
  }
  consumirAviso(NOMBRE_SUBIR_FOTOS_A_ML);
  let resultado: ResultadoDeSubida | null;
  try {
    const id = await deps.subir({
      itemId,
      variationId,
      fotos: imagenes.map((imagenId, n) => ({ imagenId, posicion: portada && n === 0 ? 'portada' : 'secundaria' })),
    });
    if (id === null) {
      return texto(`Esas fotos ya estaban subidas (o subiéndose) a "${pub.titulo}": no las subí de nuevo. Decíselo así.`);
    }
    resultado = await deps.esperarSubida(id);
  } catch (err) {
    return texto(textoDeError(err));
  }
  if (!resultado) {
    return texto('La subida sigue en curso. Decí que la estás subiendo y que en unos minutos se ve en su publicación; NUNCA que ya está.');
  }
  const subidas = resultado.subidas ?? 0;
  const errores = resultado.errores ?? [];
  const secundaria = resultado.como_secundaria?.[0];
  const partes = [
    subidas > 0 ? `Subí ${subidas === 1 ? '1 foto' : `${subidas} fotos`} a "${pub.titulo}".` : 'No se subió ninguna foto.',
    secundaria ? `La que pidió de portada fue como secundaria: ${secundaria.motivos.join(' ')}` : '',
    errores.length ? `No se pudo con ${errores.length}: ${errores[0]!.motivo}` : '',
    subidas > 0 ? 'En Mercado Libre puede aparecer "procesando" unos minutos: es normal.' : '',
  ];
  return texto(partes.filter(Boolean).join(' '));
}

/** Clave estable de un pedido de cambio (la descripción, resumida). */
function claveDeCambio(itemId: string, precio: number | null, descripcion: string | null): string {
  let h = 0;
  for (const ch of descripcion ?? '') h = (h * 31 + ch.charCodeAt(0)) | 0;
  return `${itemId}:${precio ?? ''}:${descripcion === null ? '' : `${descripcion.length}.${h}`}`;
}

const SEGUNDO_SI = `${NOMBRE_CAMBIAR_DATOS_EN_ML}:aviso_de_mercadolibre`;

export async function resolverCambiarDatosEnMl(input: unknown, deps: DepsMercadoLibre, ctx: CtxConfirmacion): Promise<Resultado> {
  const i = (input ?? {}) as { publicacion?: unknown; precio?: unknown; descripcion?: unknown; confirmado?: unknown };
  const itemId = typeof i.publicacion === 'string' ? i.publicacion.trim() : '';
  if (!RE_ITEM.test(itemId)) {
    return texto(`Falta la publicación. Buscala con \`${NOMBRE_VER_PUBLICACIONES_ML}\` o preguntale cuál.`);
  }
  const precio = typeof i.precio === 'number' && Number.isFinite(i.precio) && i.precio > 0 ? Math.round(i.precio * 100) / 100 : null;
  const descripcion = typeof i.descripcion === 'string' && i.descripcion.trim() ? i.descripcion : null;
  if (precio === null && descripcion === null) return texto('Falta qué cambiar: el precio, la descripción o las dos.');
  const clave = claveDeCambio(itemId, precio, descripcion);
  const confirmado = i.confirmado === true;
  const permisoDeMl = puedeActuar({ accion: SEGUNDO_SI, clave, confirmado, turno: ctx.turno, ultimoMensaje: ctx.ultimoMensaje });
  const permiso = permisoDeMl || puedeActuar({ accion: NOMBRE_CAMBIAR_DATOS_EN_ML, clave, confirmado, turno: ctx.turno, ultimoMensaje: ctx.ultimoMensaje });

  if (!permiso) {
    anotarAviso(NOMBRE_CAMBIAR_DATOS_EN_ML, clave, ctx.turno);
    const que = [
      precio !== null ? `el precio a $ ${precio.toLocaleString('es-AR')}` : '',
      descripcion !== null ? 'la descripción (en texto plano: sin emojis ni formato)' : '',
    ]
      .filter(Boolean)
      .join(' y ');
    return texto(
      `NO CAMBIÉ NADA TODAVÍA. Voy a cambiar ${que} de la publicación ${itemId}. Se ve apenas termina y desde Atlas no se deshace; si tiene variantes, el precio cambia en todas. Contáselo y preguntale si lo cambio. Si en su próximo mensaje dice que sí, volvé a llamarme igual con confirmado en true.`,
    );
  }
  consumirAviso(NOMBRE_CAMBIAR_DATOS_EN_ML);
  consumirAviso(SEGUNDO_SI);
  let resultado: { precio?: ResultadoCampo; descripcion?: ResultadoCampo } | null;
  try {
    const id = await deps.cambiar({ itemId, precio, descripcion, confirmado: permisoDeMl });
    resultado = await deps.esperarCambio(id);
  } catch (err) {
    const code = (err as { code?: unknown } | null)?.code;
    // Mercado Libre avisa algo que la persona no sabía (un precio muy distinto, una promoción que se cae):
    // hace falta OTRO sí, con esto a la vista.
    if (code === 'cambio_grande' || code === 'saca_promocion') {
      anotarAviso(SEGUNDO_SI, clave, ctx.turno);
      return texto(
        `NO CAMBIÉ NADA. Antes de aplicar, Mercado Libre avisa: ${(err as Error).message} Contáselo con tus palabras y preguntale si sigue. Si en su próximo mensaje dice que sí, volvé a llamarme igual con confirmado en true.`,
      );
    }
    if (code === 'precio_automatico' || code === 'precio_invalido' || code === 'descripcion_vacia') {
      return texto(`${(err as Error).message} Decíselo así.`);
    }
    return texto(textoDeError(err));
  }
  if (!resultado) return texto('El cambio se está aplicando. Decí que lo estás cambiando y que en unos minutos se ve; NUNCA que ya está.');
  const partes: string[] = [];
  if (resultado.precio) partes.push(resultado.precio.ok ? 'El precio quedó cambiado.' : `El precio no se cambió: ${resultado.precio.motivo}`);
  if (resultado.descripcion) {
    partes.push(resultado.descripcion.ok ? 'La descripción quedó cambiada.' : `La descripción no se cambió: ${resultado.descripcion.motivo}`);
  }
  return texto(partes.join(' ') || 'Listo.');
}

export async function resolverVerCalidadMl(input: unknown, deps: DepsMercadoLibre): Promise<Resultado> {
  const i = (input ?? {}) as { publicacion?: unknown };
  const itemId = typeof i.publicacion === 'string' ? i.publicacion.trim() : '';
  if (!RE_ITEM.test(itemId)) {
    return texto(`Falta la publicación. Buscala con \`${NOMBRE_VER_PUBLICACIONES_ML}\` o preguntale cuál.`);
  }
  let calidad: CalidadDeMl;
  try {
    calidad = await deps.calidad(itemId);
  } catch (err) {
    return texto(textoDeError(err));
  }
  const visitas = await deps.visitas(itemId).catch(() => null);
  if (calidad.sin_calcular) {
    return texto(`Mercado Libre todavía no calculó la calidad de esa publicación.${visitas !== null ? ` Tuvo ${visitas} visitas en 30 días.` : ''}`);
  }
  const recomendaciones = calidad.recomendaciones.length
    ? `\nLo que le falta según Mercado Libre:\n${calidad.recomendaciones.map((r) => `- ${r.titulo}${r.modo === 'advertencia' ? ' (importante)' : ''}`).join('\n')}`
    : '';
  const fotos = calidad.faltan_fotos
    ? '\nMercado Libre pide más fotos: es el momento de ofrecer generarlas (generar cuesta créditos como siempre; subirlas no).'
    : '';
  return texto(
    `Calidad en Mercado Libre: ${calidad.nivel ?? '—'} (${calidad.puntaje ?? '—'}/100).${visitas !== null ? ` Visitas en 30 días: ${visitas}.` : ''}${recomendaciones}${fotos}`,
  );
}
